import React, { useEffect } from "react";
import ProductTable from "../../components/product/ProductTable";
import PodProductTable from "../../components/product/PodProductTable";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Grid2,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import PageTitle from "../../components/common/pages/PageTitle";
import { observer } from "mobx-react";
import { useNotification } from "../../../utils/NotificationsContext";
import { useDebounce } from "../../components/common/hooks/Debounce";
import ListingFiltersPanel from "../../components/product/ListingFiltersPanel";
import AppButton from "../../components/common/buttons/AppButton";
import {
  FILTER_PRODUCTS_TYPE_OPTIONS,
  FilterProductsType,
} from "../../../models/Products/Product";
import {
  DEFAULT_PRICE_RANGE,
} from "../../../stores/ProductListingStore";

const activeFilters = [
  { id: "true", value: "Active" },
  { id: "false", value: "Inactive" },
];

const Product: React.FC = () => {
  const { productStore, podCatalogStore, productListingStore: ls } =
    useStores();
  const location = useLocation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();

  const debouncedSearchTerm = useDebounce(ls.searchTerm, 500);
  const debouncedPodSearch = useDebounce(ls.podSearchInput, 500);

  useEffect(() => {
    ls.hydrateFromSearchParams(new URLSearchParams(location.search));
    return () => ls.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once per mount
  }, []);

  useEffect(() => {
    ls.applyDebouncedProductSearch(debouncedSearchTerm.trim());
  }, [debouncedSearchTerm, ls]);

  useEffect(() => {
    ls.applyDebouncedPodSearch(debouncedPodSearch.trim());
  }, [debouncedPodSearch, ls]);

  useEffect(() => {
    ls.syncPriceInputsFromRange();
  }, [ls.priceRange, ls]);

  useEffect(() => {
    ls.syncPodPriceInputsFromRange();
  }, [ls.podPriceRange, ls]);

  useEffect(() => {
    ls.syncSelectedCategoriesFromParams();
  }, [ls.params.category_id, ls]);

  useEffect(() => {
    ls.syncFilterStateFromParams();
  }, [ls.params.active, ls.params.fast_procurement, ls]);

  useEffect(() => {
    if (ls.listingTab !== 1) return;
    ls.setPodLoading(true);
    podCatalogStore
      .fetchPodCatalog(ls.podParams)
      .catch((err: unknown) => {
        const e = err as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const message =
          e?.response?.data?.message ||
          e?.message ||
          "Unable to load POD catalog.";
        podCatalogStore.setPodCatalogListError(message);
        showError("Error occurred", message);
      })
      .finally(() => ls.setPodLoading(false));
  }, [ls.listingTab, ls.podParams, podCatalogStore, showError, ls]);

  useEffect(() => {
    if (ls.listingTab !== 0 && ls.listingTab !== 1) return;

    const syncCategoryFilters = () => {
      const mapCategories = () =>
        ls.setCategoryFilters(
          productStore.categories.map((category) => ({
            id: String(category.id),
            value: category.name,
          }))
        );

      if (productStore.categories.length === 0) {
        productStore.getCategories().then(mapCategories);
      } else {
        mapCategories();
      }
    };

    syncCategoryFilters();
  }, [ls.listingTab, productStore, ls]);

  useEffect(() => {
    if (ls.listingTab !== 0) return;
    ls.setIsLoading(true);
    productStore.getProducts(ls.params).finally(() => ls.setIsLoading(false));
  }, [ls.params, productStore, ls.listingTab, ls]);

  useEffect(() => {
    if (ls.listingTab !== 0) return;

    const searchParams = new URLSearchParams();
    Object.entries(ls.params).forEach(([key, value]) => {
      if (
        value !== undefined &&
        (!Array.isArray(value) || value.length > 0)
      ) {
        const paramValue = Array.isArray(value)
          ? value.join(",")
          : value.toString();
        searchParams.set(key, paramValue);
      }
    });

    const queryString = searchParams.toString();
    if (queryString) {
      navigate({ search: queryString }, { replace: true });
    }
  }, [ls.params, navigate, ls.listingTab, ls]);

  const handleCreate = () => {
    productStore.reset();
    navigate("/products/add-edit-product");
  };

  const duplicateProduct = (id: number) => {
    productStore
      .duplicateProduct(id)
      .then((res) => {
        showSuccess(`${res.message} Product Id -${res.result}`);
      })
      .catch((err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showError(
          "Error occurred",
          e?.response?.data?.message ||
            "Something went wrong! Please try again later."
        );
      });
  };

  const handleAddToPod = (productId: number) => {
    ls.setAddToPodLoadingId(productId);
    podCatalogStore
      .addToPod(productId)
      .then(async (res) => {
        if (res.success) {
          showSuccess(res.message);
          productStore.setProductPodCatalogStatus(productId, true);
          try {
            await podCatalogStore.fetchPodCatalog(ls.podParams);
          } catch {
            //
          }
        } else {
          showError("Error occurred", res.message || "Request failed.");
        }
      })
      .catch((err: unknown) => {
        const e = err as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        showError(
          "Error occurred",
          e?.response?.data?.message ||
            e?.message ||
            "Something went wrong! Please try again later."
        );
      })
      .finally(() => ls.setAddToPodLoadingId(null));
  };

  const handleRemoveFromPod = (productId: number) => {
    ls.setRemovingFromPodProductId(productId);
    podCatalogStore
      .removeFromPod(productId)
      .then(async (res) => {
        if (res.success) {
          showSuccess(res.message);
          productStore.setProductPodCatalogStatus(productId, false);
          try {
            await podCatalogStore.fetchPodCatalog(ls.podParams);
          } catch {
            //
          }
        } else {
          showError("Error occurred", res.message || "Request failed.");
        }
      })
      .catch((err: unknown) => {
        const e = err as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        showError(
          "Error occurred",
          e?.response?.data?.message ||
            e?.message ||
            "Something went wrong! Please try again later."
        );
      })
      .finally(() => ls.setRemovingFromPodProductId(null));
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <Paper>
        <Box
          sx={{
            marginTop: "0.6rem",
            paddingX: 4,
            paddingY: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
            borderRadius: "4px",
            minHeight: { xs: "60vh", md: window.innerHeight - 110 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingTop: 0,
              paddingBottom: 0,
            }}
          >
            <PageTitle title="Products" />
            {ls.listingTab === 0 && (
              <AppButton
                variant="primary"
                label="Add Product"
                onClick={() => handleCreate()}
              />
            )}
          </Box>
          {ls.listingTab === 1 && (
            <ListingFiltersPanel
              marginBottom="1rem"
              expanded={ls.isPodFiltersExpanded}
              onToggleExpanded={() => ls.togglePodFiltersExpanded()}
              searchValue={ls.podSearchInput}
              onSearchChange={(v) => ls.setPodSearchInput(v)}
              onSearchSubmit={() => ls.patchPodParams({ page_no: 1 })}
              categoryFilters={ls.categoryFilters}
              selectedCategoryIds={ls.podSelectedCategoriesIds}
              onSelectedCategoryIdsChange={(ids) =>
                ls.setPodSelectedCategoriesIds(ids)
              }
              showActive={false}
              showPrice={true}
              priceRangeLabel="Price Range"
              minPrice={DEFAULT_PRICE_RANGE[0]}
              maxPrice={DEFAULT_PRICE_RANGE[1]}
              priceRange={ls.podPriceRange}
              onPriceRangeChange={(range) => ls.setPodPriceRange(range)}
              minPriceInput={ls.podMinPriceInput}
              maxPriceInput={ls.podMaxPriceInput}
              onMinPriceInputChange={(v) => ls.setPodMinPriceInput(v)}
              onMaxPriceInputChange={(v) => ls.setPodMaxPriceInput(v)}
              onApply={() => ls.handlePodApply()}
              onClear={() => ls.resetPodFilters()}
            />
          )}
          {ls.listingTab === 0 && (
            <ListingFiltersPanel
              expanded={ls.isFiltersExpanded}
              onToggleExpanded={() => ls.toggleFiltersExpanded()}
              searchValue={ls.searchTerm}
              onSearchChange={(v) => ls.handleSearchBar(v)}
              onSearchKeyDown={(e) => ls.handleKeyDownSearch(e)}
              onSearchSubmit={() => ls.handleSearchSubmit()}
              categoryFilters={ls.categoryFilters}
              selectedCategoryIds={ls.selectedCategoriesIds}
              onSelectedCategoryIdsChange={(ids) =>
                ls.setSelectedCategoriesIds(ids)
              }
              showActive={true}
              activeFilters={activeFilters}
              activeValue={ls.pendingActive === false ? "false" : "true"}
              onActiveValueChange={(id) =>
                ls.setPendingActive(id === "true")
              }
              showProductsTypeFilter={true}
              productsTypeFilters={FILTER_PRODUCTS_TYPE_OPTIONS}
              productsTypeValue={ls.filterProductsType}
              onProductsTypeValueChange={(id) =>
                ls.setFilterProductsType(
                  id === FilterProductsType.FastProcurementProduct
                    ? FilterProductsType.FastProcurementProduct
                    : FilterProductsType.AllProducts,
                )
              }
              showPrice={true}
              priceRangeLabel="Price Range"
              minPrice={DEFAULT_PRICE_RANGE[0]}
              maxPrice={DEFAULT_PRICE_RANGE[1]}
              priceRange={ls.priceRange}
              onPriceRangeChange={(range) => ls.setPriceRange(range)}
              minPriceInput={ls.minPriceInput}
              maxPriceInput={ls.maxPriceInput}
              onMinPriceInputChange={(v) => ls.handleMinPriceChange(v)}
              onMaxPriceInputChange={(v) => ls.handleMaxPriceChange(v)}
              onApply={() => ls.handleApplyFilters()}
              onClear={() => ls.resetFilters()}
              marginBottom="1rem"
            />
          )}
          <Tabs
            value={ls.listingTab}
            onChange={(_, v) => ls.setListingTab(v)}
            sx={{
              mb: "1rem",
              borderBottom: 1,
              borderColor: "divider",
              minHeight: "auto",
              "& .MuiTab-root": {
                py: 1,
                minHeight: "auto",
              },
            }}
          >
            <Tab label="Products" />
            <Tab label="POD Products" />
          </Tabs>
          {ls.listingTab === 1 && (
            <Box sx={{ maxHeight: window.innerHeight - 180 }}>
              {ls.podLoading && (
                <Box
                  sx={{
                    minHeight: "40vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              {!ls.podLoading && (
                <Box>
                  {podCatalogStore.list.length === 0 ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="10rem"
                      textAlign="center"
                    >
                      <Typography
                        variant="h6"
                        fontWeight="500"
                        color="text.secondary"
                      >
                        No POD catalog rows found
                      </Typography>
                    </Box>
                  ) : (
                    <PodProductTable
                      maxHeight={window.innerHeight - 350}
                      items={podCatalogStore.list}
                      limit={ls.podParams.limit ?? podCatalogStore.limit}
                      page_no={ls.podParams.page_no ?? podCatalogStore.page_no}
                      total_count={podCatalogStore.total_count}
                      sortBy={ls.podParams.sort_by ?? "created_date"}
                      sortType={ls.podParams.sort_type ?? "DESC"}
                      onPageChange={(_e, page) =>
                        ls.podHandlePageChange(page)
                      }
                      onRowsPerPageChange={(limit) =>
                        ls.podHandleRowsPerPageChange(limit)
                      }
                      onSortChange={(column, sortType) =>
                        ls.podSortChange(column, sortType)
                      }
                      onRemoveFromPod={handleRemoveFromPod}
                      removingProductId={ls.removingFromPodProductId}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}
          {ls.listingTab === 0 && (
            <Box sx={{ maxHeight: window.innerHeight - 180 }}>
              {ls.isLoading && (
                <Box
                  sx={{
                    minHeight: "60vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              {!ls.isLoading && (
                <Box>
                  {productStore.products.length === 0 && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="10rem"
                      textAlign="center"
                    >
                      <Typography
                        variant="h6"
                        fontWeight="500"
                        color="text.secondary"
                      >
                        No data found
                      </Typography>
                    </Box>
                  )}
                  {productStore.products.length > 0 && (
                    <ProductTable
                      maxHeight={window.innerHeight - 350}
                      products={productStore.products}
                      limit={ls.params.limit || productStore.limit}
                      page_no={ls.params.page_no}
                      total_count={productStore.total_count}
                      total_pages={productStore.total_pages}
                      sortBy={ls.params.sortBy}
                      sortType={ls.params.sortType}
                      onPageChange={(_e, page) => ls.handlePageChange(page)}
                      onRowsPerPageChange={(limit) =>
                        ls.handleRowsPerPageChange(limit)
                      }
                      onSortChange={(column, sortType) =>
                        ls.productsSortChange(column, sortType)
                      }
                      handleDuplicate={(id) => duplicateProduct(id)}
                      onAddToPod={handleAddToPod}
                      addToPodLoadingId={ls.addToPodLoadingId}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Grid2>
  );
};

export default observer(Product);
